import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { IconPickerInline } from './IconPickerInline'
import type { WorkItemTypeDocument } from '@/lib/db/rxdb/schemas/workItemType'
import { BaseFormDialog } from '@/components/common/BaseFormDialog'
import { ColorPicker, PRESET_COLORS } from '@/components/app/pickers/color-picker'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { WorkItemTypeIcon } from '@/components/app/tasks/WorkItemTypeIcon'
import { useWorkItemTypeMutations } from '@/lib/db/rxdb/hooks/entities/work-item-types'
import useLoginStore from '@/store/LoginStore'

type TFunc = (key: string) => string

const buildWorkItemTypeFormSchema = (t: TFunc) =>
  z.object({
    name: z
      .string()
      .min(2, t('workItemTypes.formErrors.nameMin'))
      .max(50, t('workItemTypes.formErrors.nameMax')),
    description: z
      .string()
      .max(200, t('workItemTypes.formErrors.descriptionMax'))
      .optional(),
    icon: z.string().min(1, t('workItemTypes.formErrors.iconRequired')),
    color: z.string().min(1, t('workItemTypes.formErrors.colorRequired')),
  })

type WorkItemTypeFormData = z.infer<ReturnType<typeof buildWorkItemTypeFormSchema>>

interface WorkItemTypeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode?: 'create' | 'edit'
  initialItem?: WorkItemTypeDocument | null
  existingTypes?: Array<{ id: string; name: string }>
}

export function WorkItemTypeDialog({
  open,
  onOpenChange,
  mode = 'create',
  initialItem = null,
  existingTypes = [],
}: WorkItemTypeDialogProps) {
  const { t } = useTranslation('settings')
  const { workspaceId } = useLoginStore()
  const { createWorkItemType, updateWorkItemType } = useWorkItemTypeMutations()
  const [duplicateError, setDuplicateError] = React.useState<string | null>(null)

  const isEditMode = mode === 'edit' && !!initialItem

  const workItemTypeFormSchema = React.useMemo(() => buildWorkItemTypeFormSchema(t), [t])
  const form = useForm<WorkItemTypeFormData>({
    resolver: zodResolver(workItemTypeFormSchema),
    defaultValues: {
      name: '',
      description: '',
      icon: 'Square',
      color: PRESET_COLORS[0],
    },
  })

  React.useEffect(() => {
    if (open && isEditMode && initialItem) {
      setDuplicateError(null)
      form.reset({
        name: initialItem.name,
        description: initialItem.description ?? '',
        icon: initialItem.icon,
        color: initialItem.color,
      })
      return
    }

    if (open && !isEditMode) {
      setDuplicateError(null)
      form.reset({
        name: '',
        description: '',
        icon: 'Square',
        color: PRESET_COLORS[0],
      })
    }

    if (!open) {
      setDuplicateError(null)
      form.reset()
    }
  }, [open, isEditMode, initialItem, form])

  const handleSubmit = async (data: WorkItemTypeFormData) => {
    if (!workspaceId) return

    const trimmedName = data.name.trim()

    const isDuplicate = existingTypes.some(
      (t) =>
        t.name.toLowerCase() === trimmedName.toLowerCase() &&
        t.id !== initialItem?.id,
    )
    if (isDuplicate) {
      setDuplicateError(t('workItemTypes.dialog.duplicateError'))
      return
    }
    setDuplicateError(null)

    try {
      const payload = {
        name: trimmedName,
        icon: data.icon,
        color: data.color,
        description: data.description?.trim() || '',
      }

      if (isEditMode && initialItem) {
        await updateWorkItemType(initialItem.id, payload)
      } else {
        await createWorkItemType({
          ...payload,
          isSystem: false,
          sortOrder: '',
        })
      }

      form.reset()
      onOpenChange(false)
    } catch {
      // TODO: Show error toast
    }
  }

  const dialogTitle = isEditMode ? t('workItemTypes.dialog.editTitle') : t('workItemTypes.dialog.createTitle')
  const dialogDescription = isEditMode
    ? t('workItemTypes.dialog.editDescription')
    : t('workItemTypes.dialog.createDescription')
  const submitLabel = isEditMode ? t('workItemTypes.dialog.editSubmit') : t('workItemTypes.dialog.createSubmit')

  const watchedIcon = form.watch('icon')
  const watchedColor = form.watch('color')

  return (
    <BaseFormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={dialogTitle}
      description={dialogDescription}
      submitLabel={submitLabel}
      isSubmitting={form.formState.isSubmitting}
      submitDisabled={!form.formState.isValid || form.formState.isSubmitting}
      maxWidth="md"
    >
      <form id="dialog-form" onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="space-y-6 py-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="wit-name">
              {t('workItemTypes.dialog.nameLabel')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="wit-name"
              {...form.register('name')}
              placeholder={t('workItemTypes.dialog.namePlaceholder')}
              maxLength={50}
              autoFocus
            />
            {(form.formState.errors.name || duplicateError) && (
              <p className="text-sm text-destructive">
                {form.formState.errors.name?.message ?? duplicateError}
              </p>
            )}
          </div>

          {/* Icon & Color */}
          <div className="space-y-3">
            <Label className="text-sm font-medium text-foreground">
              {t('workItemTypes.dialog.iconColorLabel')}
            </Label>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 rounded-md border border-input bg-card px-3 py-2">
                <WorkItemTypeIcon icon={watchedIcon} color={watchedColor} className="size-5" />
                <span className="text-sm font-medium">
                  {form.watch('name') || t('workItemTypes.dialog.previewName')}
                </span>
              </div>
            </div>
            <IconPickerInline
              value={watchedIcon}
              onChange={(iconName) => form.setValue('icon', iconName, { shouldValidate: true })}
            />
            <ColorPicker
              value={watchedColor}
              onChange={(color) => form.setValue('color', color ?? PRESET_COLORS[0])}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="wit-description">{t('workItemTypes.dialog.descriptionLabel')}</Label>
            <textarea
              id="wit-description"
              {...form.register('description')}
              placeholder={t('workItemTypes.dialog.descriptionPlaceholder')}
              maxLength={200}
              rows={3}
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
            />
            {form.formState.errors.description && (
              <p className="text-sm text-destructive">
                {form.formState.errors.description.message}
              </p>
            )}
          </div>
        </div>
      </form>
    </BaseFormDialog>
  )
}
